import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  pageStyle,
  colors,
  typography,
  buttons,
  radius,
  shadows,
} from "../../theme";
import { formatDateRus } from "../../utils/formatDateRus";
import Spinner from "../common/Spinner";

function DatePage() {
  const { id, date } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromArchive =
    location.state && location.state.fromArchive === true;

  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);

  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [transcribing, setTranscribing] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const saveTimerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordIntervalRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const latestTextRef = useRef("");
  const creatingNoteRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const res = await fetch(`/api/students/${id}/notes`);
        if (!res.ok) throw new Error("Failed to load notes");
        const all = await res.json();
        if (cancelled) return;

        const existing = all.find(n => n.date === date);

        if (existing) {
          setNote(existing);
          latestTextRef.current = existing.text || "";
        } else {
          const placeholder = {
            id: null,
            student_id: parseInt(id, 10),
            date,
            text: "",
          };
          setNote(placeholder);
          latestTextRef.current = "";
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;

      if (recordIntervalRef.current) {
        clearInterval(recordIntervalRef.current);
        recordIntervalRef.current = null;
      }

      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
        autoStopTimerRef.current = null;
      }

      const rec = mediaRecorderRef.current;
      if (rec && rec.state === "recording") {
        try {
          rec.stop();
        } catch (e) {
          console.error(e);
        }
      }
      // Do not clear saveTimerRef here, let pending save finish
    };
  }, [id, date]);

  const scheduleSave = (noteId, text) => {
    if (!noteId) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(async () => {
      try {
        await fetch(`/api/notes/${noteId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
      } catch (e) {
        console.error(e);
      } finally {
        saveTimerRef.current = null;
      }
    }, 250);
  };

  const ensureNoteCreated = async () => {
    if (note && note.id) return note;
    if (creatingNoteRef.current) return null;

    creatingNoteRef.current = true;
    try {
      const textAtCreation = latestTextRef.current || "";

      const res = await fetch(`/api/students/${id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, text: textAtCreation }),
      });
      const created = await res.json();

      setNote(prev => {
        if (!prev) {
          return { ...created, text: latestTextRef.current || "" };
        }
        return { ...prev, id: created.id };
      });

      const finalText = latestTextRef.current || "";
      const trimmedFinal = finalText.trim();

      if (created.id && trimmedFinal) {
        try {
          await fetch(`/api/notes/${created.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text: finalText }),
          });
        } catch (e) {
          console.error(e);
        }
      }

      return created;
    } catch (e) {
      console.error(e);
      return null;
    } finally {
      creatingNoteRef.current = false;
    }
  };

  const updateNoteText = async newText => {
    if (!note) return;

    latestTextRef.current = newText;

    setNote(prev => (prev ? { ...prev, text: newText } : prev));

    const trimmed = (newText || "").trim();

    if (!note.id) {
      if (!trimmed) {
        return;
      }
      await ensureNoteCreated();
      return;
    }

    const currentId = note.id;

    if (!trimmed) {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      try {
        await fetch(`/api/notes/${currentId}`, {
          method: "DELETE",
        });
      } catch (e) {
        console.error(e);
      }
      setNote(prev =>
        prev ? { ...prev, id: null, text: "" } : prev
      );
      latestTextRef.current = "";
      return;
    }

    scheduleSave(currentId, newText);
  };

  const handleTextareaChange = e => {
    const value = e.target.value;
    updateNoteText(value);
  };

  const startRecording = async () => {
    if (!note) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      const chunks = [];

      recorder.ondataavailable = e => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = async () => {
        if (recordIntervalRef.current) {
          clearInterval(recordIntervalRef.current);
          recordIntervalRef.current = null;
        }
        if (autoStopTimerRef.current) {
          clearTimeout(autoStopTimerRef.current);
          autoStopTimerRef.current = null;
        }
        setRecording(false);
        setRecordSeconds(0);

        if (!chunks.length) return;

        const blob = new Blob(chunks, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("file", blob, "note.webm");

        setTranscribing(true);
        try {
          const res = await fetch("/api/transcribe", {
            method: "POST",
            body: formData,
          });
          const data = await res.json();
          const text = data.text || "";
          if (!text.trim()) return;

          const base = latestTextRef.current || "";
          const combined = base ? base + "\n" + text : text;

          await updateNoteText(combined);
        } catch (e) {
          console.error(e);
        } finally {
          setTranscribing(false);
        }
      };

      recorder.start();
      setRecording(true);
      setRecordSeconds(0);

      recordIntervalRef.current = setInterval(() => {
        setRecordSeconds(prev => prev + 1);
      }, 1000);

      autoStopTimerRef.current = setTimeout(() => {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state === "recording") {
          try {
            rec.stop();
          } catch (e) {
            console.error(e);
          }
        }
      }, 5 * 60 * 1000);
    } catch (e) {
      console.error(e);
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === "recording") {
      try {
        rec.stop();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleDeleteNoteConfirmed = async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    if (note && note.id) {
      try {
        await fetch(`/api/notes/${note.id}`, {
          method: "DELETE",
        });
      } catch (e) {
        console.error(e);
      }
    }

    latestTextRef.current = "";
    setShowDeleteModal(false);
    navigate(`/student/${id}`);
  };

  const handleBack = async () => {
    if (note && note.id && saveTimerRef.current) {
      const textToSave = latestTextRef.current;
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      try {
        await fetch(`/api/notes/${note.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: textToSave }),
        });
      } catch (e) {
        console.error(e);
      }
    }
    navigate(`/student/${id}`, {
      state: { fromArchive: cameFromArchive },
    });
  };

  if (loading || !note) {
    return (
      <div
        style={{
          background: colors.bg,
          minHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        <div style={pageStyle}>
          <button
            onClick={handleBack}
            style={{
              ...buttons.subtle,
              fontSize: 24,
              marginBottom: 18,
              borderRadius: radius.pill,
            }}
          >
            ← Назад к ученику
          </button>
          <div
            style={{
              paddingTop: 40,
            }}
          >
            <Spinner size={40} />
          </div>
        </div>
      </div>
    );
  }

  const niceDate = formatDateRus(date);
  const hasText = (note.text || "").trim().length > 0;
  const hasNoteRecord = !!note.id && hasText;

  const recordLabel = hasText ? "Дополнить заметку" : "Диктовать заметку";

  const minutes = Math.floor(recordSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (recordSeconds % 60).toString().padStart(2, "0");
  const timerLabel = `${minutes}:${seconds}`;

  return (
    <div
      style={{
        background: colors.bg,
        minHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      <div style={pageStyle}>
        <button
          onClick={handleBack}
          style={{
            ...buttons.subtle,
            fontSize: 24,
            marginBottom: 18,
            borderRadius: radius.pill,
          }}
        >
          ← Назад к ученику
        </button>

        <h1
          style={{
            fontSize: 34,
            marginBottom: 14,
            color: colors.textMain,
          }}
        >
          {niceDate}
        </h1>

        <div style={{ display: "flex", gap: 16, marginBottom: 18 }}>
          {!recording && !transcribing && (
            <button
              onClick={startRecording}
              style={{
                ...buttons.primary,
                fontSize: 24,
                padding: "12px 20px",
                borderRadius: radius.button,
              }}
            >
              🎙 {recordLabel}
            </button>
          )}

          {recording && (
            <button
              onClick={stopRecording}
              style={{
                ...buttons.danger,
                fontSize: 24,
                padding: "12px 20px",
                borderRadius: radius.button,
              }}
            >
              ■ Стоп {timerLabel}
            </button>
          )}

          {!recording && transcribing && (
            <button
              type="button"
              disabled
              style={{
                ...buttons.secondary,
                fontSize: 24,
                padding: "12px 20px",
                borderRadius: radius.button,
                display: "flex",
                alignItems: "center",
                gap: 10,
                opacity: 0.9,
              }}
            >
              <Spinner size={22} />
              <span>Идёт расшифровка…</span>
            </button>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <textarea
            value={note.text || ""}
            onChange={handleTextareaChange}
            style={{
              width: "100%",
              minHeight: 420,
              fontSize: 36,
              padding: 10,
              borderRadius: radius.card,
              border: `1px solid ${colors.borderSoft}`,
              boxSizing: "border-box",
              background: colors.card,
              boxShadow: shadows.card,
            }}
          />
        </div>

        {hasNoteRecord && (
          <div
            style={{
              marginTop: 40,
              paddingTop: 18,
              borderTop: `1px solid ${colors.borderSoft}`,
            }}
          >
            <button
              onClick={() => setShowDeleteModal(true)}
              style={{
                ...buttons.dangerSoft,
                fontSize: 22,
                padding: "10px 18px",
                borderRadius: radius.button,
              }}
            >
              Удалить заметку
            </button>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 16,
            zIndex: 9999,
            overflowX: "hidden",
          }}
        >
          <div
            style={{
              background: colors.card,
              borderRadius: radius.card,
              padding: 24,
              width: "90%",
              maxWidth: 380,
              boxSizing: "border-box",
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.5)",
              fontSize: typography.body,
              color: colors.textMain,
            }}
          >
            <h2
              style={{
                marginTop: 0,
                marginBottom: 12,
                fontSize: typography.sectionTitle,
              }}
            >
              Удалить заметку
            </h2>
            <p
              style={{
                margin: 0,
                marginBottom: 18,
              }}
            >
              Эта встреча и заметка будут удалены
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
              }}
            >
              <button
                onClick={handleDeleteNoteConfirmed}
                style={{
                  ...buttons.danger,
                  flex: 1,
                  fontSize: 20,
                  padding: "10px 0",
                }}
              >
                Удалить
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  ...buttons.secondary,
                  flex: 1,
                  fontSize: 20,
                  padding: "10px 0",
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePage;
